import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getMedicalRoleLabel, getMedicalRoleOptions } from '../utils/medicalRoles';
import { getGenderedUserRole } from '../constants/medicalTerminology';
import { FileText, Eye, Clock, CheckCircle, Archive } from 'lucide-react';
import { AREAS_OF_INTEREST } from '../../../shared/constants/index.js';

interface OwnedCase {
  id: string;
  title: string;
  caseType: string;
  status: 'open' | 'resolved' | 'archived';
  createdAt: string;
  updatedAt: string;
  viewCount: number;
  commentCount: number;
}

export function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [ownedCases, setOwnedCases] = useState<OwnedCase[]>([]);
  const [casesLoading, setCasesLoading] = useState(false);

  // Edit profile state
  const [editData, setEditData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    hospitalAffiliation: user?.hospitalAffiliation || '',
    medicalRole: user?.medicalRole || '',
    areasOfInterest: user?.areasOfInterest || []
  });

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Fetch owned cases when component mounts
  useEffect(() => {
    if (user?.id) {
      fetchOwnedCases();
    }
  }, [user?.id]);

  // Update edit data when user data changes
  useEffect(() => {
    if (user) {
      setEditData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        hospitalAffiliation: user.hospitalAffiliation || '',
        medicalRole: user.medicalRole || '',
        areasOfInterest: user.areasOfInterest || []
      });
    }
  }, [user]);

  const fetchOwnedCases = async () => {
    try {
      setCasesLoading(true);
      const token = localStorage.getItem('accessToken');
      if (!token) return;

      const response = await fetch(`/api/v1/cases?userId=${user?.id}&limit=50`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setOwnedCases(data.data?.cases || []);
      }
    } catch (error) {
      console.error('Error fetching owned cases:', error);
    } finally {
      setCasesLoading(false);
    }
  };

  const handleAreaOfInterestChange = (area: string) => {
    setEditData(prev => ({
      ...prev,
      areasOfInterest: prev.areasOfInterest.includes(area)
        ? prev.areasOfInterest.filter(s => s !== area)
        : [...prev.areasOfInterest, area]
    }));
  };

  const handleEditProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setMessage({ type: 'error', text: 'Δεν είστε συνδεδεμένος' });
        return;
      }

      const response = await fetch('/api/v1/auth?action=update-profile', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firstName: editData.firstName,
          lastName: editData.lastName,
          hospitalAffiliation: editData.hospitalAffiliation,
          medicalRole: editData.medicalRole,
          areasOfInterest: editData.areasOfInterest
        })
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: 'Το προφίλ ενημερώθηκε επιτυχώς' });
        setShowEditModal(false);
        // Refresh user data from the server
        await refreshUser();
      } else {
        setMessage({ type: 'error', text: data.message || 'Σφάλμα κατά την ενημέρωση' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Σφάλμα δικτύου' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage({ type: '', text: '' });

    // Validation
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Οι κωδικοί δεν ταιριάζουν' });
      setIsSubmitting(false);
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Ο κωδικός πρέπει να έχει τουλάχιστον 8 χαρακτήρες' });
      setIsSubmitting(false);
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setMessage({ type: 'error', text: 'Δεν είστε συνδεδεμένος' });
        return;
      }

      const response = await fetch('/api/v1/auth?action=change-password', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: 'Ο κωδικός άλλαξε επιτυχώς' });
        setShowPasswordModal(false);
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        setMessage({ type: 'error', text: data.message || 'Σφάλμα κατά την αλλαγή κωδικού' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Σφάλμα δικτύου' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">Προφίλ Χρήστη</h1>
          <p className="mt-4 text-gray-600">Φόρτωση...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white shadow-sm rounded-lg">
        <div className="px-6 py-6 border-b border-gray-200">
          <h1 className="text-3xl font-bold text-gray-900">Προφίλ Χρήστη</h1>
        </div>
        
        <div className="px-6 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Personal Information */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Προσωπικά Στοιχεία
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Όνομα
                  </label>
                  <div className="mt-1 text-sm text-gray-900">
                    {user.firstName || 'Δεν έχει οριστεί'}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Επώνυμο
                  </label>
                  <div className="mt-1 text-sm text-gray-900">
                    {user.lastName || 'Δεν έχει οριστεί'}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <div className="mt-1 text-sm text-gray-900">
                    {user.email}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Φύλο
                  </label>
                  <div className="mt-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.sex === 'female' ? 'bg-pink-100 text-pink-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {user.sex === 'female' ? 'Γυναίκα' : 'Άνδρας'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Professional Information */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Επαγγελματικά Στοιχεία
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Νοσοκομείο/Ίδρυμα
                  </label>
                  <div className="mt-1 text-sm text-gray-900">
                    {user.hospitalAffiliation || 'Δεν έχει οριστεί'}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Ρόλος
                  </label>
                  <div className="mt-1 flex flex-wrap gap-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.role === 'admin' ? 'bg-red-100 text-red-800' :
                      user.role === 'moderator' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {getGenderedUserRole(user.role as any, user.sex)}
                    </span>
                    {user.medicalRole && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {getMedicalRoleLabel(user.medicalRole, user.sex)}
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Περιοχές Ενδιαφέροντος
                  </label>
                  <div className="mt-1">
                    {user.areasOfInterest && user.areasOfInterest.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {user.areasOfInterest.map((area, index) => (
                          <span 
                            key={index}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-medical-100 text-medical-800"
                          >
                            {area}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">
                        Δεν έχουν οριστεί περιοχές ενδιαφέροντος
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Account Information */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Πληροφορίες Λογαριασμού
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Κατάσταση Επαλήθευσης
                </label>
                <div className="mt-1">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user.isVerified ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {user.isVerified ? '✓ Επαληθευμένος' : '✗ Μη επαληθευμένος'}
                  </span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Μέλος από
                </label>
                <div className="mt-1 text-sm text-gray-900">
                  {new Date(user.createdAt).toLocaleDateString('el-GR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Owned Cases Section */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Τα Περιστατικά Μου
              </h2>
              <Link 
                to="/cases/new"
                className="text-sm bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 transition-colors"
              >
                Νέο Περιστατικό
              </Link>
            </div>
            
            {casesLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : ownedCases.length > 0 ? (
              <div className="space-y-3">
                {ownedCases.map((case_) => {
                  const getStatusIcon = (status: string) => {
                    switch (status) {
                      case 'open':
                        return <Clock className="w-4 h-4 text-blue-500" />;
                      case 'resolved':
                        return <CheckCircle className="w-4 h-4 text-green-500" />;
                      case 'archived':
                        return <Archive className="w-4 h-4 text-gray-500" />;
                      default:
                        return <FileText className="w-4 h-4 text-gray-500" />;
                    }
                  };

                  const getStatusLabel = (status: string) => {
                    switch (status) {
                      case 'open':
                        return 'Ανοιχτό';
                      case 'resolved':
                        return 'Επιλυμένο';
                      case 'archived':
                        return 'Αρχειοθετημένο';
                      default:
                        return status;
                    }
                  };

                  const getStatusColor = (status: string) => {
                    switch (status) {
                      case 'open':
                        return 'bg-blue-100 text-blue-800';
                      case 'resolved':
                        return 'bg-green-100 text-green-800';
                      case 'archived':
                        return 'bg-gray-100 text-gray-800';
                      default:
                        return 'bg-gray-100 text-gray-800';
                    }
                  };

                  return (
                    <div key={case_.id} className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <Link 
                              to={`/cases/${case_.id}`}
                              className="text-lg font-medium text-blue-600 hover:text-blue-800 hover:underline truncate"
                            >
                              {case_.title}
                            </Link>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(case_.status)}`}>
                              {getStatusIcon(case_.status)}
                              <span className="ml-1">{getStatusLabel(case_.status)}</span>
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center">
                              <FileText className="w-4 h-4 mr-1" />
                              {case_.caseType}
                            </span>
                            <span className="flex items-center">
                              <Eye className="w-4 h-4 mr-1" />
                              {case_.viewCount} προβολές
                            </span>
                            <span>{case_.commentCount} σχόλια</span>
                            <span>
                              Δημιουργήθηκε: {new Date(case_.createdAt).toLocaleDateString('el-GR')}
                            </span>
                            {case_.updatedAt !== case_.createdAt && (
                              <span>
                                Ενημερώθηκε: {new Date(case_.updatedAt).toLocaleDateString('el-GR')}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 ml-4">
                          <Link
                            to={`/cases/${case_.id}`}
                            className="text-blue-600 hover:text-blue-800 p-2 rounded-md hover:bg-blue-50 transition-colors"
                            title="Προβολή περιστατικού"
                          >
                            <Eye className="w-4 h-4" />
                          </Link>
                          {case_.status === 'open' && (
                            <Link
                              to={`/cases/${case_.id}/edit`}
                              className="text-gray-600 hover:text-gray-800 p-2 rounded-md hover:bg-gray-100 transition-colors"
                              title="Επεξεργασία περιστατικού"
                            >
                              <FileText className="w-4 h-4" />
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>Δεν έχετε δημιουργήσει περιστατικά ακόμα.</p>
                <Link 
                  to="/cases/new"
                  className="inline-block mt-2 text-blue-600 hover:text-blue-800 font-medium"
                >
                  Δημιουργήστε το πρώτο σας περιστατικό
                </Link>
              </div>
            )}
          </div>

          {/* Message Display */}
          {message.text && (
            <div className={`mt-6 p-4 rounded-md ${
              message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 
              'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {message.text}
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row gap-4">
              <button 
                onClick={() => setShowEditModal(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Επεξεργασία Προφίλ
              </button>
              <button 
                onClick={() => setShowPasswordModal(true)}
                className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
              >
                Αλλαγή Κωδικού
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Επεξεργασία Προφίλ</h3>
            
            <form onSubmit={handleEditProfile} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Όνομα
                </label>
                <input
                  type="text"
                  value={editData.firstName}
                  onChange={(e) => setEditData({...editData, firstName: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Επώνυμο
                </label>
                <input
                  type="text"
                  value={editData.lastName}
                  onChange={(e) => setEditData({...editData, lastName: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Νοσοκομείο/Ίδρυμα
                </label>
                <input
                  type="text"
                  value={editData.hospitalAffiliation}
                  onChange={(e) => setEditData({...editData, hospitalAffiliation: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ιατρικός Ρόλος
                </label>
                <select
                  value={editData.medicalRole}
                  onChange={(e) => setEditData({...editData, medicalRole: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Επιλέξτε ρόλο</option>
                  {getMedicalRoleOptions(user?.sex || 'male').map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Περιοχές Ενδιαφέροντος
                </label>
                <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3">
                  {AREAS_OF_INTEREST.map((area) => (
                    <label key={area} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editData.areasOfInterest.includes(area)}
                        onChange={() => handleAreaOfInterestChange(area)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">{area}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? 'Αποθήκευση...' : 'Αποθήκευση'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Ακύρωση
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Αλλαγή Κωδικού</h3>
            
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Τρέχων Κωδικός
                </label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Νέος Κωδικός
                </label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                  minLength={8}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Επιβεβαίωση Νέου Κωδικού
                </label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                  minLength={8}
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? 'Αλλαγή...' : 'Αλλαγή Κωδικού'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Ακύρωση
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}